/**
 * Memory detail view (`/memories/m/:id`).
 *
 * Renders the title + description + ordered photo grid for a single
 * memory event. Author-only inline edit covers the title, description,
 * and the photo set — add existing photos, upload new ones, or remove
 * them, then PATCH the replacement set. Delete affordance below. Photo
 * carousel/lightbox reuses /memories/:id (the existing PhotoDetailPage)
 * so tapping a photo lands in the standard viewer.
 */
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, photoBytesUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  memoryDetailQueryOptions,
  photoGridQueryOptions,
  type PhotoGridResponse,
  type PhotoListItem,
} from '../lib/queries';
import { ScreenHeader } from '../components/ScreenHeader';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { IconButton } from '../components/IconButton';
import { Button } from '../components/Button';
import { IconArrowLeft, IconImage, IconPencil, IconTrash } from '../components/icons';
import { renderWithMentions } from '../components/MentionTextarea';
import { uploadPhoto, type UploadResult } from '../lib/photo-upload';

export default function MemoryDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const me = auth.state === 'signed-in' ? auth.me : null;
  const qc = useQueryClient();
  const memory = useQuery(memoryDetailQueryOptions(id));

  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  // Working photo set while editing — an ordered list seeded from the
  // memory, then toggled (add existing / remove) and appended to by
  // uploads. PATCH sends it as a full replacement.
  const [editPhotoIds, setEditPhotoIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const data = memory.data;
  const isAuthor = !!(me && data && data.sharedBy && data.sharedBy === me.member.id);

  // Family photo pool for the edit picker. Only fetched while editing.
  const grid = useQuery({ ...photoGridQueryOptions(null), enabled: editing });

  const beginEdit = () => {
    if (!data) return;
    setDraftTitle(data.title);
    setDraftDescription(data.description ?? '');
    setEditPhotoIds(data.photoIds);
    setEditing(true);
  };

  const togglePhoto = (pid: string) => {
    setEditPhotoIds((prev) =>
      prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid],
    );
  };

  // Upload a new photo straight from the edit screen: it lands in the
  // family pool (optimistic pending tile) and appends to this memory's
  // working set on success.
  const uploadInto = useMutation<
    UploadResult,
    Error,
    File,
    { tempId: string; blobUrl: string }
  >({
    onMutate: async (file) => {
      const tempId = `tmp_${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
      const blobUrl = URL.createObjectURL(file);
      await qc.cancelQueries({ queryKey: ['photo-grid'] });
      qc.setQueryData<PhotoGridResponse>(['photo-grid', null], (old) => ({
        photos: [
          { id: tempId, url: blobUrl, takenAt: Date.now(), pending: true },
          ...(old?.photos ?? []),
        ],
        nextCursor: old?.nextCursor ?? null,
      }));
      return { tempId, blobUrl };
    },
    mutationFn: (file) => uploadPhoto(file, { type: 'family' }),
    onSuccess: (result, _file, ctx) => {
      if (ctx) URL.revokeObjectURL(ctx.blobUrl);
      setEditPhotoIds((prev) =>
        prev.includes(result.photoId) ? prev : [...prev, result.photoId],
      );
      void qc.invalidateQueries({ queryKey: ['photo-grid'] });
    },
    onError: (_err, _file, ctx) => {
      if (!ctx) return;
      qc.setQueryData<PhotoGridResponse>(['photo-grid', null], (old) => ({
        photos: (old?.photos ?? []).filter((p) => p.id !== ctx.tempId),
        nextCursor: old?.nextCursor ?? null,
      }));
      URL.revokeObjectURL(ctx.blobUrl);
    },
  });

  const update = useMutation({
    mutationFn: () => {
      // Keep the cover valid: if the current cover was removed from the
      // set, fall back to the first remaining photo.
      const cover =
        data?.coverPhotoId && editPhotoIds.includes(data.coverPhotoId)
          ? data.coverPhotoId
          : editPhotoIds[0];
      return api.patch(`/memories/${id}`, {
        title: draftTitle.trim(),
        description: draftDescription.trim() ? draftDescription.trim() : null,
        photoIds: editPhotoIds,
        coverPhotoId: cover,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['memories', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['memories', 'list'] });
      setEditing(false);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/memories/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['memories', 'list'] });
      navigate('/memories', { replace: true });
    },
  });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const formattedDate = useMemo(() => {
    if (!data?.eventDate) return null;
    return new Date(data.eventDate + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }, [data?.eventDate]);

  if (memory.isLoading) {
    return (
      <>
        <ScreenHeader title="Memory" />
        <div className="px-5 pt-5">
          <p className="text-soft text-sm">Loading…</p>
        </div>
      </>
    );
  }
  if (memory.error || !data) {
    return (
      <>
        <ScreenHeader title="Memory" />
        <div className="px-5 pt-5">
          <p className="text-soft text-sm">This memory could not be loaded.</p>
          <Link
            to="/memories"
            className="text-oxblood text-sm underline mt-3 inline-block"
          >
            Back to Memories
          </Link>
        </div>
      </>
    );
  }

  // Edit picker tiles: the family pool, with any of this memory's photos
  // that fell off the recent grid page prepended so they always stay
  // visible (and removable) even if they're old.
  type Tile = PhotoListItem & { pending?: boolean };
  const pool: Tile[] = grid.data?.photos ?? [];
  const poolIds = new Set(pool.map((p) => p.id));
  const olderInMemory: Tile[] = editPhotoIds
    .filter((pid) => !poolIds.has(pid))
    .map((pid) => ({ id: pid, url: '', takenAt: null }));
  const editTiles: Tile[] = [...olderInMemory, ...pool];
  const canSave = !!draftTitle.trim() && editPhotoIds.length > 0 && !update.isPending;

  return (
    <>
      <ScreenHeader
        title="Memory"
        right={
          isAuthor && !editing ? (
            <IconButton icon={<IconPencil />} label="Edit" onClick={beginEdit} />
          ) : undefined
        }
      />
      <div className="px-5 pt-3 pb-8">
        <div className="-ml-2 mb-1">
          <IconButton
            icon={<IconArrowLeft />}
            label="Back to Memories"
            onClick={() => {
              // Cold deep-links (history length 1) don't have a previous
              // route to pop back to, so fall through to the Memories
              // index rather than stranding the user.
              if (window.history.length > 1) navigate(-1);
              else navigate('/memories');
            }}
          />
        </div>
        {editing ? (
          <div className="space-y-3">
            <input
              type="text"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              maxLength={120}
              className="w-full h-11 px-3 rounded-xl border border-line bg-card text-ink font-serif text-xl focus:outline-none focus:border-oxblood"
            />
            <textarea
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="Description (optional)"
              className="w-full p-3 rounded-xl border border-line bg-card text-ink placeholder:text-muted focus:outline-none focus:border-oxblood resize-none"
            />

            {/* Photo management: upload new, or tap to add/remove. */}
            <div>
              <label className="kicker text-sage mb-2 block">
                {editPhotoIds.length} {editPhotoIds.length === 1 ? 'photo' : 'photos'}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (e.target) e.target.value = '';
                  for (const f of files) uploadInto.mutate(f);
                }}
              />
              {grid.isLoading ? (
                <p className="text-soft text-sm italic">Loading your library…</p>
              ) : (
                <div className="grid grid-cols-4 gap-2 max-h-[40vh] overflow-y-auto">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square w-full rounded-md border-2 border-dashed border-line text-soft flex flex-col items-center justify-center gap-1 hover:border-oxblood hover:text-oxblood transition-colors"
                    aria-label="Upload photos"
                  >
                    <IconImage size={28} />
                    <span className="text-[11px] leading-none">Upload</span>
                  </button>
                  {editTiles.map((p) => {
                    if (p.pending) {
                      return (
                        <div
                          key={p.id}
                          className="aspect-square w-full rounded-md bg-line"
                        />
                      );
                    }
                    const isSelected = editPhotoIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => togglePhoto(p.id)}
                        aria-pressed={isSelected}
                        className={`aspect-square w-full overflow-hidden rounded-md bg-line block relative ${
                          isSelected ? 'ring-4 ring-oxblood' : ''
                        }`}
                      >
                        <img
                          src={photoBytesUrl(p.id, 'thumb')}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                        <span
                          aria-hidden
                          className={`absolute top-1 right-1 w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs ${
                            isSelected
                              ? 'bg-oxblood border-oxblood text-paper'
                              : 'bg-paper/80 border-paper'
                          }`}
                        >
                          {isSelected ? '✓' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-muted mt-1.5">
                Tap to add or remove. Upload adds new photos to this memory.
              </p>
            </div>

            {update.isError && (
              <p className="text-sm text-oxblood">
                Couldn't save. Make sure the memory has at least one photo.
              </p>
            )}
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => update.mutate()}
                disabled={!canSave}
                busy={update.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="font-serif text-3xl text-ink leading-tight">{data.title}</h1>
            {formattedDate && <p className="kicker text-sage mt-1">{formattedDate}</p>}
            {data.description && (
              <p className="mt-4 text-ink font-serif text-base leading-relaxed whitespace-pre-wrap">
                {renderWithMentions(data.description)}
              </p>
            )}
          </>
        )}

        {!editing && (
          <div className="mt-6 grid grid-cols-3 gap-2">
            {data.photoIds.map((pid) => (
              <Link
                key={pid}
                to={`/memories/${pid}?memoryId=${id}`}
                className="aspect-square w-full overflow-hidden rounded-lg bg-line block"
              >
                <img
                  src={photoBytesUrl(pid, 'thumb')}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </Link>
            ))}
          </div>
        )}

        {isAuthor && !editing && (
          <div className="mt-8 pt-4 border-t border-line flex justify-start -ml-2">
            <IconButton
              icon={<IconTrash />}
              label="Delete memory"
              variant="destructive"
              busy={remove.isPending}
              onClick={() => setConfirmDelete(true)}
            />
          </div>
        )}

        {confirmDelete && (
          <ConfirmDialog
            title="Delete this memory?"
            message="The underlying photos are kept; only the memory entry is removed."
            confirmLabel="Delete"
            destructive
            busy={remove.isPending}
            error={remove.isError ? 'Could not delete. Try again.' : null}
            onConfirm={() => remove.mutate()}
            onCancel={() => setConfirmDelete(false)}
          />
        )}
      </div>
    </>
  );
}
